import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import { Eraser } from "lucide-react";

/**
 * Touch / mouse / stylus signature capture on an HTML5 canvas.
 * Exposes `getDataUrl()` and `clear()` via ref.
 * Renders a solid orange stroke on a white background for maximum contrast in the PDF.
 */
const SignaturePad = forwardRef(function SignaturePad(
  { width = 700, height = 220, strokeColor = "#0a0a0a", strokeWidth = 2.5, onChange, "data-testid": testId = "signature-pad" },
  ref
) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  // Ensure crisp lines on retina displays and reset the drawing context.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = strokeWidth;
  }, [strokeColor, strokeWidth]);

  const pos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) {
      setEmpty(false);
      onChange && onChange(false);
    }
  };

  const end = () => {
    drawing.current = false;
    last.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setEmpty(true);
    onChange && onChange(true);
  };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    clear,
    getDataUrl: () => (empty ? "" : canvasRef.current.toDataURL("image/png")),
  }));

  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        data-testid={testId}
        style={{ width: "100%", height: `${height}px`, touchAction: "none" }}
        className="bg-white border border-[#333] block cursor-crosshair"
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <button
        type="button"
        onClick={clear}
        data-testid={`${testId}-clear`}
        className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1 bg-white/95 border border-[#e5e5e5] text-[#333] font-mono-tech text-[10px] uppercase hover:bg-[#ff4b00] hover:text-white hover:border-[#ff4b00]"
      >
        <Eraser size={11} /> Cancella
      </button>
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none font-mono-tech text-[11px] text-[#999] uppercase tracking-wider">
          Firma qui con dito o pennino
        </div>
      )}
    </div>
  );
});

export default SignaturePad;
